<template>
    <v-card flat color="transparent" class="fill-height w-100">
        <v-card-title v-if="showTitle">
            <span class="secondary--text">All Spaces</span>
        </v-card-title>
        <v-card-text>
            <div v-if="!fetchingSpaces">
                <div class="d-flex align-center">
                    <SpaceCreateMenu v-if="canCreateSpaces" offset-y :disabled="fetchingSpaces" @select="createSpaceAndApp">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" color="primary" class="mr-2" :disabled="fetchingSpaces">
                                <v-icon left>mdi-plus</v-icon>
                                Space
                                <v-icon right>mdi-chevron-down</v-icon>
                            </v-btn>
                        </template>
                    </SpaceCreateMenu>

                    <v-spacer></v-spacer>

                    <v-text-field
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense
                        placeholder="Search for a space..."
                        v-model="searchSpace"
                        clearable
                        class="mr-2">
                        <template v-slot:append>
                            <SpaceTypeFilterMenu v-model="selectedSpaceTypes" :show-inactive.sync="showInactive" />
                        </template>
                    </v-text-field>

                    <v-btn icon @click="$store.dispatch('orgStore/fetchOrgSpaces', $route.params.oid)">
                        <v-icon>refresh</v-icon>
                    </v-btn>
                </div>

                <v-data-table
                    :headers="headers"
                    :items="allSpacesSorted"
                    item-key="sid"
                    :items-per-page.sync="itemsPerPage"
                    :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                    :search="searchSpace"
                    :item-class="setRowClass"
                    class="mt-4 all-spaces-table">
                    <template v-slot:no-data>
                        <span>No spaces found.</span>
                    </template>

                    <template v-slot:[`item.space_long_id`]="{ item }">
                        <v-card flat color="transparent" class="my-1 d-inline-flex">
                            <v-list-item
                                two-line
                                class="rounded overflow-hidden"
                                :class="setRowClass(item)"
                                :to="{
                                    name: 'snapshot-overview',
                                    params: { oid: $route.params.oid, sid: item.sid, iid: item.iid, snid: item.snid }
                                }">
                                <v-list-item-content class="d-inline-block">
                                    <v-list-item-title>
                                        <b>{{ item.space_long_id }}</b>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ item.space_description }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-icon right color="primary" class="showOnHover">mdi-chevron-right</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                        </v-card>
                    </template>

                    <template v-slot:[`item.space_type`]="{ item }">
                        <v-chip
                            label
                            :disabled="Boolean(item.space_archival_timestamp || item.space_rested_timestamp)"
                            :class="spaceTypeMetaBySpace(item).buttonColor">
                            <v-icon small left :class="spaceTypeMetaBySpace(item).iconColorClass">
                                {{ spaceTypeMetaBySpace(item).icon }}
                            </v-icon>
                            <span :class="spaceTypeMetaBySpace(item).iconColorClass">
                                {{ spaceTypeMetaBySpace(item).label }}
                            </span>
                        </v-chip>
                    </template>

                    <template v-slot:[`item.space_creation_timestamp`]="{ item }">
                        <div v-if="item.space_creation_timestamp" class="text-no-wrap">{{ item.space_creation_timestamp | dateTimeToHuman }}</div>
                        <div v-else>-</div>
                    </template>

                    <template v-slot:[`item.phase`]="{ item }">
                        <div class="pr-5">
                            <v-tooltip v-if="item.space_archival_timestamp" top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-chip small color="secondary" outlined v-bind="attrs" v-on="on">archived</v-chip>
                                </template>
                                <span>Archived on {{ item.space_archival_timestamp | dateTimeToHuman }}</span>
                            </v-tooltip>
                            <v-tooltip v-else-if="item.space_rested_timestamp" top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-chip small color="secondary" outlined v-bind="attrs" v-on="on">resting</v-chip>
                                </template>
                                <span>Resting since {{ item.space_rested_timestamp | dateTimeToHuman }}</span>
                            </v-tooltip>
                            <v-chip v-else small color="success" outlined>live</v-chip>
                        </div>
                    </template>

                    <template v-slot:[`item.space_role`]="{ item }">
                        <v-chip outlined color="grey" small>{{ spaceRole(item.space_role).label }}</v-chip>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                        <div class="text-right">
                            <v-menu offset-y left>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text color="secondary" small v-bind="attrs" v-on="on">
                                        <v-icon>mdi-dots-horizontal</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense nav>
                                    <v-list-item @click="gotoEditSpace(item)">
                                        <v-list-item-icon>
                                            <v-icon>mdi-pencil-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Edit</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="gotoSpaceResources(item)">
                                        <v-list-item-icon>
                                            <v-icon>mdi-sitemap-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Resources</v-list-item-title>
                                    </v-list-item>
                                    <SpaceInviteDialog>
                                        <template v-slot:activator="{ attrs, open }">
                                            <v-list-item v-bind="attrs" :disabled="!canManageUsers(item)" @click.stop="open">
                                                <v-list-item-icon>
                                                    <v-icon>mdi-account-plus-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4">Invite users</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </SpaceInviteDialog>
                                    <v-list-item @click="toggleFavorite(item)">
                                        <v-list-item-icon>
                                            <v-icon>{{ favoriteActionIcon(item) }}</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">{{ favoriteActionLabel(item) }}</v-list-item-title>
                                    </v-list-item>
                                    <v-divider class="my-2"></v-divider>
                                    <EntityDeleteDialog
                                        entity-type="space"
                                        :organization-id="$route.params.oid"
                                        :space-id="item.sid"
                                        :space-name="item.space_long_id">
                                        <template v-slot:activator="{ attrs, open }">
                                            <v-list-item v-bind="attrs" :disabled="!canManageUsers(item)" @click.stop="open">
                                                <v-list-item-icon>
                                                    <v-icon color="error">mdi-trash-can-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4 error--text">Delete</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </EntityDeleteDialog>
                                </v-list>
                            </v-menu>
                        </div>
                    </template>
                </v-data-table>
            </div>

            <div v-else>
                <div
                    v-for="item in [
                        { id: 'c1', opacity: 1 },
                        { id: 'c2', opacity: 0.75 },
                        { id: 'c3', opacity: 0.5 }
                    ]"
                    :key="item.id">
                    <div :style="{ opacity: item.opacity }">
                        <v-skeleton-loader type="list-item-three-line" class="mx-auto" />
                    </div>
                </div>
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { isAdminRole, spaceRole } from '@/utils'
import { enumsData } from '@/mixins/enums'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { SPACE_TYPE_META, SPACE_TYPE_ORDER, DEFAULT_SPACE_TYPE_META } from '@/utils/spaceTypeMeta'
import EntityDeleteDialog from '@/components/EntityDeleteDialog.vue'
import SpaceInviteDialog from '@/modules/space/components/SpaceInviteDialog.vue'
const SpaceCreateMenu = () => import('@/components/SpaceCreateMenu.vue')
const SpaceTypeFilterMenu = () => import('@/components/SpaceTypeFilterMenu.vue')

const unixEpoch = DateTime.fromMillis(0).toISO()

export default {
    name: 'AllSpacesTable',
    mixins: [enumsData],
    props: {
        showTitle: {
            type: Boolean,
            default: true
        }
    },
    components: {
        EntityDeleteDialog,
        SpaceInviteDialog,
        SpaceCreateMenu,
        SpaceTypeFilterMenu
    },
    data() {
        return {
            searchSpace: '',
            showInactive: true,
            selectedSpaceTypes: SPACE_TYPE_ORDER.slice(),
            itemsPerPage: 100,
            itemsPerPageOptions: [-1, 10, 50, 100],
            headers: [
                { text: 'Type', value: 'space_type', width: '120px' },
                { text: 'Name', value: 'space_long_id', class: 'pl-8' },
                { text: 'Creation time', value: 'space_creation_timestamp', width: '260px' },
                { text: 'Your role', value: 'space_role', width: '150px' },
                { text: 'Status', value: 'phase', align: 'right', width: '130px' },
                { text: 'Actions', value: 'actions', align: 'right', width: '80px', sortable: false }
            ]
        }
    },
    computed: {
        ...mapGetters('orgStore', ['isOrgFaculty', 'isOrgManager']),
        ...mapState(['userMetadata']),
        ...mapState('orgStore', ['educationSpaces', 'researchSpaces', 'datasetSpaces', 'fetchingSpaces']),
        spaceTypeOrder() {
            return SPACE_TYPE_ORDER
        },
        canCreateSpaces() {
            return this.isOrgFaculty || this.isOrgManager
        },
        favoriteSpaceSids() {
            if (!Array.isArray(this.userMetadata?.favorite_space_sids)) {
                return []
            }
            return this.userMetadata.favorite_space_sids.map(sid => this.normalizeSid(sid)).filter(Boolean)
        },
        allSpaces() {
            let spaces = []

            if (this.selectedSpaceTypes.includes('courses')) {
                spaces = spaces.concat(this.educationSpaces)
            }
            if (this.selectedSpaceTypes.includes('projects')) {
                spaces = spaces.concat(this.researchSpaces)
            }
            if (this.selectedSpaceTypes.includes('datasets')) {
                spaces = spaces.concat(this.datasetSpaces)
            }

            let filteredSpaces = spaces
            if (this.showInactive) {
                return filteredSpaces
            }
            filteredSpaces = filteredSpaces.filter(space => !space.space_archival_timestamp && !space.space_rested_timestamp)
            return filteredSpaces
        },
        allSpacesSorted() {
            return orderBy(
                this.allSpaces,
                [space => Boolean(space.space_archival_timestamp || space.space_rested_timestamp), space => space.space_creation_timestamp || unixEpoch],
                ['asc', 'desc']
            )
        }
    },
    methods: {
        normalizeSid(sid) {
            if (sid === null || sid === undefined) return null
            const numericSid = Number(sid)
            if (Number.isFinite(numericSid)) {
                return String(numericSid)
            }
            return String(sid).trim()
        },
        spaceTypeMetaByKey(key) {
            return SPACE_TYPE_META[key] || DEFAULT_SPACE_TYPE_META
        },
        getSpaceTypeKey(space) {
            const sid = space?.sid?.toString()
            if (!sid) return null

            return (
                this.spaceTypeOrder.find(key => {
                    const meta = this.spaceTypeMetaByKey(key)
                    const spaces = this[meta.spacesStateKey]
                    return Array.isArray(spaces) && spaces.some(item => item.sid?.toString() === sid)
                }) || null
            )
        },
        spaceTypeMetaBySpace(space) {
            return this.spaceTypeMetaByKey(this.getSpaceTypeKey(space))
        },
        createSpaceAndApp(type) {
            this.$store.dispatch('homeStore/updateCreateSpaceAndApp', {
                operationSelectedVal: true,
                stepVal: 1,
                spaceTypeVal: type
            })
            if (type === 'class') {
                this.$router.push({
                    name: 'home-add-class',
                    params: { oid: this.$route.params.oid }
                })
            } else if (type === 'research') {
                this.$router.push({
                    name: 'home-add-research-project',
                    params: { oid: this.$route.params.oid }
                })
            } else if (type === 'dataset') {
                this.$router.push({
                    name: 'home-add-dataset',
                    params: { oid: this.$route.params.oid }
                })
            }
        },
        isFavorite(item) {
            const sid = this.normalizeSid(item?.sid)
            return !!sid && this.favoriteSpaceSids.includes(sid)
        },
        favoriteActionLabel(item) {
            return this.isFavorite(item) ? 'Remove from favourites' : 'Add to favourites'
        },
        favoriteActionIcon(item) {
            return this.isFavorite(item) ? 'mdi-star-off-outline' : 'mdi-star-outline'
        },
        canManageUsers(item) {
            return isAdminRole(item?.space_role)
        },
        gotoEditSpace(item) {
            this.$router.push({
                name: 'config-space-general',
                params: {
                    oid: this.$route.params.oid,
                    sid: item.sid
                }
            })
        },
        gotoSpaceResources(item) {
            this.$router.push({
                name: 'config-space-resource',
                params: {
                    oid: this.$route.params.oid,
                    sid: item.sid
                }
            })
        },
        toggleFavorite(item) {
            const sid = this.normalizeSid(item?.sid)
            if (!sid) return

            const currentFavorites = this.favoriteSpaceSids
            const updatedFavorites = currentFavorites.includes(sid)
                ? currentFavorites.filter(currentSid => currentSid !== sid)
                : [sid, ...currentFavorites.filter(currentSid => currentSid !== sid)]

            this.$store.commit('setUserMetadata', { favorite_space_sids: updatedFavorites })
        },
        spaceRole,
        setRowClass(item) {
            if (item.space_archival_timestamp || item.space_rested_timestamp) {
                return 'text--disabled'
            }
            return ''
        }
    }
}
</script>

<style scoped lang="scss">
.all-spaces-table {
    ::v-deep .v-data-table__wrapper > table {
        table-layout: fixed;
        width: 100%;
    }
}
</style>
